import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Star, Search, Filter, ChevronDown, ChevronRight, X, MessageSquare, Activity,
         ListTodo, EyeOff, SlidersHorizontal, Tag, Square, Target, Milestone, BookOpen } from "lucide-react";

export function TaskFilterBar({
  filters, updateFilters,
  isFiltersExpanded, setIsFiltersExpanded,
  setIsQuickProgressOpen, setIsProgressLogOpen,
  categories, teamMembers,
  selectedTasks, setSelectedTasks,
  isBulkActionsOpen, setIsBulkActionsOpen,
  setPerTaskCompletedOverrides,
  taskStats,
  uniqueAssigneeNames = [],
}: any) {
  return (
        <Collapsible open={isFiltersExpanded} onOpenChange={setIsFiltersExpanded} className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-foreground">Search & Filters</h3>
                <div className="flex items-center gap-2">
                  <Button
                    variant={filters.showTopThreeOnly ? "default" : "outline"}
                    size="sm"
                    onClick={() => updateFilters({ showTopThreeOnly: !filters.showTopThreeOnly })}
                    data-testid="button-quick-filter-starred"
                    className={filters.showTopThreeOnly ? "bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-500" : ""}
                  >
                    <Star className="h-4 w-4 mr-1" fill={filters.showTopThreeOnly ? "white" : "none"} />
                    Starred
                  </Button>
                  <Button
                    variant={filters.showSubtasks ? "default" : "outline"}
                    size="sm"
                    onClick={() => { updateFilters({ showSubtasks: !filters.showSubtasks }); setPerTaskCompletedOverrides(new Set()); }}
                    data-testid="button-toggle-subtasks"
                    className={filters.showSubtasks ? "bg-blue-500 hover:bg-blue-600 text-white border-blue-500" : ""}
                  >
                    <ListTodo className="h-4 w-4 mr-1" />
                    Sub-tasks
                  </Button>
                  {filters.showSubtasks && (
                    <Button
                      variant={filters.hideCompletedSubtasks ? "default" : "outline"}
                      size="sm"
                      onClick={() => { updateFilters({ hideCompletedSubtasks: !filters.hideCompletedSubtasks, perTaskHideCompleted: [] }); setPerTaskCompletedOverrides(new Set()); }}
                      data-testid="button-hide-completed-subtasks"
                      className={filters.hideCompletedSubtasks ? "bg-slate-600 hover:bg-slate-700 text-white border-slate-600" : ""}
                    >
                      <EyeOff className="h-4 w-4 mr-1" />
                      Hide Done
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsQuickProgressOpen(true)}
                    data-testid="button-quick-progress"
                  >
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Quick Progress
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsProgressLogOpen(true)}
                    data-testid="button-open-progress-log"
                  >
                    <Activity className="h-4 w-4 mr-1" />
                    View Progress Log
                  </Button>
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-9 h-9 p-0 rounded-full border-2 border-red-500 bg-red-50 hover:bg-red-100 dark:bg-red-950 dark:hover:bg-red-900"
                      data-testid="button-toggle-filters"
                    >
                      {isFiltersExpanded ? (
                        <ChevronDown className="h-4 w-4 text-red-600 dark:text-red-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-red-600 dark:text-red-400" />
                      )}
                      <span className="sr-only">Toggle filters</span>
                    </Button>
                  </CollapsibleTrigger>
                </div>
              </div>
              <CollapsibleContent>
                <div className="space-y-4">
                  {/* Filter Tabs - Available in both List and Kanban views */}
                  <div className="inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground w-full">
                    <button
                      type="button"
                      onClick={() => updateFilters({ filterTab: "all" })}
                      className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 flex-1 gap-1 ${
                        filters.filterTab === "all" || !filters.filterTab 
                          ? "bg-background text-foreground shadow-sm" 
                          : ""
                      }`}
                      data-testid="filter-tab-all"
                    >
                      <Filter className="h-4 w-4" />
                      <span className="hidden sm:inline">All</span>
                      <Badge variant="secondary" className="ml-1">{taskStats.total}</Badge>
                    </button>
                    <button
                      type="button"
                      onClick={() => updateFilters({ filterTab: "standalone" })}
                      className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 flex-1 gap-1 ${
                        filters.filterTab === "standalone" 
                          ? "bg-background text-foreground shadow-sm" 
                          : ""
                      }`}
                      data-testid="filter-tab-standalone"
                    >
                      <Square className="h-4 w-4" />
                      <span className="hidden sm:inline">Standalone</span>
                      <Badge variant="secondary" className="ml-1">{taskStats.standalone}</Badge>
                    </button>
                    <button
                      type="button"
                      onClick={() => updateFilters({ filterTab: "goals" })}
                      className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 flex-1 gap-1 ${
                        filters.filterTab === "goals" 
                          ? "bg-background text-foreground shadow-sm" 
                          : ""
                      }`}
                      data-testid="filter-tab-goals"
                    >
                      <Target className="h-4 w-4" />
                      <span className="hidden sm:inline">Goals</span>
                      <Badge variant="secondary" className="ml-1">{taskStats.linkedToGoals}</Badge>
                    </button>
                    <button
                      type="button"
                      onClick={() => updateFilters({ filterTab: "milestones" })}
                      className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 flex-1 gap-1 ${
                        filters.filterTab === "milestones" 
                          ? "bg-background text-foreground shadow-sm" 
                          : ""
                      }`}
                      data-testid="filter-tab-milestones"
                    >
                      <Milestone className="h-4 w-4" />
                      <span className="hidden sm:inline">Milestones</span>
                      <Badge variant="secondary" className="ml-1">{taskStats.linkedToMilestones}</Badge>
                    </button>
                    <button
                      type="button"
                      onClick={() => updateFilters({ filterTab: "journal" })}
                      className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 flex-1 gap-1 ${
                        filters.filterTab === "journal" 
                          ? "bg-background text-foreground shadow-sm" 
                          : ""
                      }`}
                      data-testid="filter-tab-journal"
                    >
                      <BookOpen className="h-4 w-4" />
                      <span className="hidden sm:inline">Journal</span>
                      <Badge variant="secondary" className="ml-1">{taskStats.linkedToJournal}</Badge>
                    </button>
                  </div>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="relative flex-1 w-full">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search tasks..."
                        value={filters.searchQuery || ""}
                        onChange={(e) => {
                          console.log("[Tasks Search] Input changed:", e.target.value);
                          updateFilters({ searchQuery: e.target.value });
                        }}
                        className="pl-10"
                        data-testid="input-search-tasks"
                      />
                    </div>
                    <Select value={filters.completionFilter || "all"} onValueChange={(v) => updateFilters({ completionFilter: v })}>
                      <SelectTrigger className="w-40" data-testid="select-completion-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-between"
                          data-testid="button-category-filter"
                        >
                          <span className="truncate">
                            {filters.categoryFilter && filters.categoryFilter.length > 0
                              ? `${filters.categoryFilter.length} ${filters.categoryFilter.length === 1 ? 'Category' : 'Categories'}`
                              : 'All Categories'}
                          </span>
                          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[200px] p-0" align="start">
                        <div className="p-2">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Categories</span>
                            {filters.categoryFilter && filters.categoryFilter.length > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-auto p-1 text-xs"
                                onClick={() => updateFilters({ categoryFilter: [] })}
                              >
                                Clear
                              </Button>
                            )}
                          </div>
                          <div className="space-y-1 max-h-[200px] overflow-y-auto">
                            {(() => {
                              const customCats = categories.filter((c: any) => c.scope !== "goals");
                              const goalCats = categories.filter((c: any) => c.scope === "goals");
                              const showHeaders = customCats.length > 0 && goalCats.length > 0;
                              const renderCat = (category: any) => (
                                <div
                                  key={category.id}
                                  className="flex items-center space-x-2 p-1 hover:bg-accent rounded cursor-pointer"
                                  onClick={() => {
                                    const currentCategories = filters.categoryFilter || [];
                                    const newCategories = currentCategories.includes(category.name)
                                      ? currentCategories.filter((c: any) => c !== category.name)
                                      : [...currentCategories, category.name];
                                    updateFilters({ categoryFilter: newCategories });
                                  }}
                                >
                                  <Checkbox
                                    checked={filters.categoryFilter?.includes(category.name) || false}
                                    onCheckedChange={() => {}}
                                    data-testid={`checkbox-category-${category.name}`}
                                  />
                                  <span className="text-sm flex-1">{category.name}</span>
                                </div>
                              );
                              return (
                                <>
                                  {showHeaders && customCats.length > 0 && (
                                    <p className="px-1 py-0.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Custom</p>
                                  )}
                                  {customCats.map(renderCat)}
                                  {showHeaders && goalCats.length > 0 && (
                                    <p className="px-1 py-0.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Goal Categories</p>
                                  )}
                                  {goalCats.map(renderCat)}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>

                    <Select 
                      value={filters.assignedToFilter || "all"} 
                      onValueChange={(v) => {
                        updateFilters({ assignedToFilter: v as "all" | "me" | "others" | "sharedWithMe" });
                      }}
                    >
                      <SelectTrigger data-testid="select-assigned-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Assignments</SelectItem>
                        <SelectItem value="me">Assigned to Me</SelectItem>
                        <SelectItem value="others">Assigned to Others</SelectItem>
                        <SelectItem value="sharedWithMe">Shared with Me</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date Range Filter */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Due Date Range</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">From</label>
                        <Input
                          type="date"
                          value={filters.dateRangeStart || ""}
                          onChange={(e) => updateFilters({ dateRangeStart: e.target.value || undefined })}
                          data-testid="input-date-range-start"
                          className="w-full"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">To</label>
                        <Input
                          type="date"
                          value={filters.dateRangeEnd || ""}
                          onChange={(e) => updateFilters({ dateRangeEnd: e.target.value || undefined })}
                          data-testid="input-date-range-end"
                          className="w-full"
                        />
                      </div>
                    </div>
                    {(filters.dateRangeStart || filters.dateRangeEnd) && (
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">
                          {filters.dateRangeStart && filters.dateRangeEnd 
                            ? `Showing tasks from ${new Date(filters.dateRangeStart).toLocaleDateString()} to ${new Date(filters.dateRangeEnd).toLocaleDateString()}`
                            : filters.dateRangeStart
                              ? `Showing tasks from ${new Date(filters.dateRangeStart).toLocaleDateString()} onwards`
                              : `Showing all past tasks until ${filters.dateRangeEnd ? new Date(filters.dateRangeEnd).toLocaleDateString() : ''}`
                          }
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto p-1 text-xs"
                          onClick={() => updateFilters({ dateRangeStart: undefined, dateRangeEnd: undefined })}
                          data-testid="button-clear-date-range"
                        >
                          Clear
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Starred Tasks Filter */}
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="show-top-three-only"
                      checked={filters.showTopThreeOnly || false}
                      onCheckedChange={(checked: any) => updateFilters({ showTopThreeOnly: checked })}
                      data-testid="switch-show-top-three-only"
                    />
                    <label
                      htmlFor="show-top-three-only"
                      className="text-sm font-medium leading-none cursor-pointer select-none flex items-center gap-2"
                    >
                      <Star className="h-4 w-4 text-yellow-500" />
                      Show Starred Tasks Only
                    </label>
                  </div>

                  {/* DISABLED: Recurring Tasks Filter - Causes production tasks to disappear */}
                  {/* TODO: Re-enable with proper safeguards to prevent filtering out legacy tasks */}
                  {/* <div className="flex items-center space-x-2">
                    <Checkbox
                      id="show-only-latest-recurring"
                      checked={filters.showOnlyLatestRecurring || false}
                      onCheckedChange={(checked: any) => updateFilters({ showOnlyLatestRecurring: checked as boolean })}
                      data-testid="checkbox-show-only-latest-recurring"
                    />
                    <label
                      htmlFor="show-only-latest-recurring"
                      className="text-sm font-medium leading-none cursor-pointer select-none"
                    >
                      Show only latest recurring tasks
                    </label>
                  </div> */}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Sort Level 1</label>
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={filters.sortBy1 || "displayOrder"} onValueChange={(v) => updateFilters({ sortBy1: v as any })}>
                          <SelectTrigger data-testid="select-sort-by-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="displayOrder">Manual Order</SelectItem>
                            <SelectItem value="dueDate">Due Date</SelectItem>
                            <SelectItem value="createdAt">Created Date</SelectItem>
                            <SelectItem value="priority">Priority</SelectItem>
                            <SelectItem value="assignedToOther">Assigned To</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={filters.sortOrder1 || "asc"} onValueChange={(v) => updateFilters({ sortOrder1: v as "asc" | "desc" })}>
                          <SelectTrigger data-testid="select-sort-order-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="asc">Asc</SelectItem>
                            <SelectItem value="desc">Desc</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-2 block">Sort Level 2</label>
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={filters.sortBy2 || "none"} onValueChange={(v) => updateFilters({ sortBy2: v as any })}>
                          <SelectTrigger data-testid="select-sort-by-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="displayOrder">Manual Order</SelectItem>
                            <SelectItem value="dueDate">Due Date</SelectItem>
                            <SelectItem value="createdAt">Created Date</SelectItem>
                            <SelectItem value="priority">Priority</SelectItem>
                            <SelectItem value="assignedToOther">Assigned To</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={filters.sortOrder2 || "asc"} onValueChange={(v) => updateFilters({ sortOrder2: v as "asc" | "desc" })}>
                          <SelectTrigger data-testid="select-sort-order-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="asc">Asc</SelectItem>
                            <SelectItem value="desc">Desc</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Sort Level 3</label>
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={filters.sortBy3 || "none"} onValueChange={(v) => updateFilters({ sortBy3: v as any })}>
                          <SelectTrigger data-testid="select-sort-by-3">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="displayOrder">Manual Order</SelectItem>
                            <SelectItem value="dueDate">Due Date</SelectItem>
                            <SelectItem value="createdAt">Created Date</SelectItem>
                            <SelectItem value="priority">Priority</SelectItem>
                            <SelectItem value="assignedToOther">Assigned To</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={filters.sortOrder3 || "asc"} onValueChange={(v) => updateFilters({ sortOrder3: v as "asc" | "desc" })}>
                          <SelectTrigger data-testid="select-sort-order-3">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="asc">Asc</SelectItem>
                            <SelectItem value="desc">Desc</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {filters.assignedToFilter === "others" && uniqueAssigneeNames.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button 
                            variant="outline" 
                            className="justify-start"
                            data-testid="button-filter-assignee-names"
                          >
                            <Filter className="h-4 w-4 mr-2" />
                            Filter by Name
                            {(filters.selectedAssigneeNames || []).length > 0 && (
                              <Badge variant="secondary" className="ml-2">
                                {(filters.selectedAssigneeNames || []).length} selected
                              </Badge>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80" align="start">
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium text-sm">Filter by Assignee</h4>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => updateFilters({ selectedAssigneeNames: uniqueAssigneeNames })}
                                  data-testid="button-select-all-assignees"
                                >
                                  Select All
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => updateFilters({ selectedAssigneeNames: [] })}
                                  data-testid="button-clear-assignees"
                                >
                                  Clear
                                </Button>
                              </div>
                            </div>
                            <div className="max-h-60 overflow-y-auto space-y-2">
                              {uniqueAssigneeNames.map((name: any) => (
                                <div key={name} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`assignee-${name}`}
                                    checked={(filters.selectedAssigneeNames || []).includes(name)}
                                    onCheckedChange={(checked: any) => {
                                      const currentNames = filters.selectedAssigneeNames || [];
                                      if (checked) {
                                        updateFilters({ selectedAssigneeNames: [...currentNames, name] });
                                      } else {
                                        updateFilters({ selectedAssigneeNames: currentNames.filter((n: any) => n !== name) });
                                      }
                                    }}
                                    data-testid={`checkbox-assignee-${name}`}
                                  />
                                  <label
                                    htmlFor={`assignee-${name}`}
                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                                  >
                                    {name}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Select value={filters.groupBy1 || "none"} onValueChange={(v) => updateFilters({ groupBy1: v })}>
                      <SelectTrigger data-testid="select-group-by-1">
                        <SelectValue placeholder="Group By Level 1" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Grouping</SelectItem>
                        <SelectItem value="category">Category</SelectItem>
                        <SelectItem value="priority">Priority</SelectItem>
                        <SelectItem value="assignedTo">Assigned To</SelectItem>
                        <SelectItem value="status">Status</SelectItem>
                        <SelectItem value="dueDate">Due Date</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={filters.groupBy2 || "none"} onValueChange={(v) => updateFilters({ groupBy2: v })}>
                      <SelectTrigger data-testid="select-group-by-2">
                        <SelectValue placeholder="Group By Level 2" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Grouping</SelectItem>
                        <SelectItem value="category">Category</SelectItem>
                        <SelectItem value="priority">Priority</SelectItem>
                        <SelectItem value="assignedTo">Assigned To</SelectItem>
                        <SelectItem value="status">Status</SelectItem>
                        <SelectItem value="dueDate">Due Date</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={filters.groupBy3 || "none"} onValueChange={(v) => updateFilters({ groupBy3: v })}>
                      <SelectTrigger data-testid="select-group-by-3">
                        <SelectValue placeholder="Group By Level 3" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Grouping</SelectItem>
                        <SelectItem value="category">Category</SelectItem>
                        <SelectItem value="priority">Priority</SelectItem>
                        <SelectItem value="assignedTo">Assigned To</SelectItem>
                        <SelectItem value="status">Status</SelectItem>
                        <SelectItem value="dueDate">Due Date</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CollapsibleContent>
        </Collapsible>
  );
}
